/* eslint-disable @typescript-eslint/no-explicit-any */
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import {
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { ReactNode, useRef, useState, useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { EFieldSortBy, EOrderSortBy, SortBy } from "@/types";

interface IProps<T> {
  rows: Array<T>;
  labels: Array<string | ReactNode>;
  page?: number;
  count?: number;
  onChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  setSort?: ({ column, direction }: SortBy) => void;
}

const DiscountTable = <T,>({
  rows,
  labels,
  page = 1,
  count = 1,
  onChange,
  setSort,
}: IProps<T>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [needsShadow, setNeedsShadow] = useState(false);
  const [activeColumn, setActiveColumn] = useState<boolean>(true);
  useEffect(() => {
    const checkScrollable = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        setNeedsShadow(scrollWidth > clientWidth);
      }
    };

    checkScrollable();

    window.addEventListener('resize', checkScrollable);

    return () => {
      window.removeEventListener('resize', checkScrollable);
    };
  }, [rows, labels]);

  const handleSort = (column: string) => {
    if (!setSort) return;
    if (activeColumn) {
      setSort({ column, direction: EOrderSortBy.ASC });
    } else {
      setSort({ column, direction: EOrderSortBy.DESC });
    }
  };

  const transformLabelToField = (label: string): string => {
    const fieldMap: Record<string, string> = {
      "Usage per code": EFieldSortBy.USAGE_LIMIT,
      "Usage per user": EFieldSortBy.USAGE_LIMIT_PER_USER,
    };

    return fieldMap[label] || label;
  };

  const actionHeaderStyle = {
    position: "sticky" as const,
    right: -6,
    zIndex: 2,
    backgroundColor: "#fff",
    boxShadow: needsShadow ? "-4px 0px 6px -2px rgba(0,0,0,0.1)" : "none",
    transition: "box-shadow 0.2s ease",
    backdropFilter: "blur(8px)",
  };

  const actionCellStyle = {
    position: "sticky" as const,
    right: -6,
    zIndex: 1,
    backgroundColor: "#fff",
    boxShadow: needsShadow ? "-4px 0px 6px -2px rgba(0,0,0,0.1)" : "none",
    transition: "box-shadow 0.2s ease",
    backdropFilter: "blur(8px)",
  };

  return (
    <div className="mb-2 flex w-full flex-col overflow-hidden">
      <TableContainer
        ref={tableContainerRef}
        className="rounded-[5px] pb-6 [&::-webkit-scrollbar-button:end]:w-48 [&::-webkit-scrollbar-button:start]:w-48 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#94A3B8] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-button-background [&::-webkit-scrollbar]:h-4"
        onScroll={() => {
          if (tableContainerRef.current) {
            const { scrollWidth, clientWidth } = tableContainerRef.current;
            setNeedsShadow(scrollWidth > clientWidth);
          }
        }}
      >
        <Table stickyHeader aria-label="sticky table text-[#020617]">
          <TableHead>
            <TableRow>
              {labels.map((label, index) => {
                const labelStr = typeof label === "string" ? label : "";
                const isActionColumn =
                  labelStr === "Action" || labelStr === "Actions";
                const isSortable =
                  labelStr === "Usage per code" ||
                  labelStr === "Usage per user";

                return (
                  <TableCell
                    className="h-[42px] bg-[#F4F4F4] py-0 text-[12px]"
                    key={index}
                    style={isActionColumn ? actionHeaderStyle : {}}
                  >
                    {isSortable ? (
                      <div
                        onClick={() => {
                          setActiveColumn(!activeColumn);
                          handleSort(transformLabelToField(labelStr));
                        }}
                        className="flex cursor-pointer items-center justify-between gap-1"
                      >
                        <p className="whitespace-nowrap font-semibold text-main">
                          {label}
                        </p>
                        <img
                          src="/images/chevron-selector-vertical.png"
                          alt="arrow"
                        />
                      </div>
                    ) : (
                      <p className="whitespace-nowrap font-semibold text-main">
                        {label}
                      </p>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, indexRow) => (
                <TableRow
                  key={indexRow}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {Object.keys(row as object).map((keyRow, index) => {
                    const isActionColumn =
                      keyRow === "action" || keyRow === "actions";
                    const isNumericColumn =
                      labels[index] === "Usage per code" ||
                      labels[index] === "Usage per user" ||
                      labels[index] === "Discount Amount";

                    return (
                      <TableCell
                        className={`text-[12px] text-primary ${isNumericColumn ? "!text-center" : ""}`}
                        key={index}
                        component="th"
                        scope="row"
                        style={isActionColumn ? actionCellStyle : {}}
                      >
                        {(row as any)[keyRow]}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={labels.length} align="center">
                  <div className="flex flex-col items-center py-6">
                    <FolderOffOutlinedIcon
                      fontSize="large"
                      className="text-gray"
                    />
                    <p className="mt-2 font-medium text-gray">Data not found</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!!count && (
        <Pagination
          shape="rounded"
          className="my-[12px] mt-[24px] self-center"
          count={count}
          page={page}
          onChange={onChange}
          siblingCount={0}
          boundaryCount={3}
          renderItem={(item) => (
            <PaginationItem
              slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
              sx={{
                backgroundColor: "transparent",
                color: "#94A3B8",
                fontSize: 16,
              }}
              {...item}
            />
          )}
        />
      )}
    </div>
  );
};

export default DiscountTable;
